import React, { useEffect, useState } from "react";
import { db } from "../firebaseConfig";
import { collection, doc, getDoc, setDoc, getDocs, addDoc, deleteDoc } from "firebase/firestore";

function Settings() {
  // --- STATES ---
  const [generalSettings, setGeneralSettings] = useState({
    monthlyTarget: 1000000,
    yearlyTarget: 12000000,
    totalLeaves: 21
  });
  
  const [holidays, setHolidays] = useState([]);
  const [newHoliday, setNewHoliday] = useState({ date: "", name: "" });
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false); // 🔥 For Add Button Loading

  // --- FETCH DATA ---
  useEffect(() => {
    fetchSettings();
    fetchHolidays();
  }, []);

  const fetchSettings = async () => {
    try {
        const docRef = doc(db, "settings", "config");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            setGeneralSettings(docSnap.data());
        }
    } catch(e) { console.log("Error fetching settings:", e); }
  };

  const fetchHolidays = async () => {
    try {
        const q = await getDocs(collection(db, "holidays"));
        const list = q.docs.map(d => ({ ...d.data(), id: d.id }));
        // Sort by Date
        list.sort((a, b) => new Date(a.date) - new Date(b.date));
        setHolidays(list);
    } catch(e) { console.log("Error fetching holidays:", e); }
  };

  // --- SAVE ACTIONS ---
  const saveGeneralSettings = async () => {
    setLoading(true);
    try {
      await setDoc(doc(db, "settings", "config"), generalSettings);
      alert("✅ Settings Saved Successfully!");
    } catch (e) {
      console.error(e);
      alert("Error saving settings: " + e.message);
    }
    setLoading(false);
  };

  const addHoliday = async (e) => {
    e.preventDefault();
    if (!newHoliday.date || !newHoliday.name) return alert("⚠️ Please select a Date and enter a Name.");
    
    setAdding(true); // Start Loading
    try {
        await addDoc(collection(db, "holidays"), {
            date: newHoliday.date,
            name: newHoliday.name
        });
        setNewHoliday({ date: "", name: "" });
        await fetchHolidays(); // Refresh List
        alert("✅ Holiday Added!");
    } catch (e) {
        console.error(e);
        alert("❌ Failed to add holiday: " + e.message);
    }
    setAdding(false); // Stop Loading
  };

  const deleteHoliday = async (id) => {
    if(window.confirm("Are you sure you want to remove this holiday?")) {
        try {
            await deleteDoc(doc(db, "holidays", id));
            fetchHolidays();
        } catch (e) {
            alert("Error removing: " + e.message);
        }
    }
  };

  return (
    <div style={{ padding: "30px", fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", backgroundColor:"#f4f7f6", minHeight:"100vh" }}>
      <h2 style={{ color: "#2c3e50", marginBottom:"30px" }}>⚙️ System Settings</h2>

      <div style={{ display: "flex", gap: "30px", flexWrap: "wrap", alignItems: "flex-start" }}>
        
        {/* --- SECTION 1: GENERAL CONFIG --- */}
        <div style={cardStyle}>
          <h3 style={headerStyle}>📊 Targets & Policies</h3>
          
          <div style={formGroup}>
            <label style={labelStyle}>Monthly Sales Target (₹)</label>
            <input 
              type="number" 
              value={generalSettings.monthlyTarget} 
              onChange={(e) => setGeneralSettings({...generalSettings, monthlyTarget: e.target.value})}
              style={inputStyle}
            />
          </div>

          <div style={formGroup}>
            <label style={labelStyle}>Yearly Sales Target (₹)</label>
            <input 
              type="number" 
              value={generalSettings.yearlyTarget} 
              onChange={(e) => setGeneralSettings({...generalSettings, yearlyTarget: e.target.value})}
              style={inputStyle}
            />
          </div>

          <div style={formGroup}>
            <label style={labelStyle}>Total Yearly Paid Leaves</label>
            <input 
              type="number" 
              value={generalSettings.totalLeaves} 
              onChange={(e) => setGeneralSettings({...generalSettings, totalLeaves: e.target.value})}
              style={inputStyle}
            />
          </div>

          <button onClick={saveGeneralSettings} style={btnSave} disabled={loading}>
            {loading ? "Saving..." : "💾 Save Changes"}
          </button>
        </div>

        {/* --- SECTION 2: HOLIDAY MANAGEMENT --- */}
        <div style={{...cardStyle, flex: 1.5}}>
          <h3 style={headerStyle}>🎉 Holiday Calendar</h3>
          
          {/* 🔥 BETTER FORM LAYOUT */}
          <form onSubmit={addHoliday} style={{ backgroundColor: "#f9f9f9", padding: "15px", borderRadius: "8px", marginBottom: "20px", border: "1px solid #eee" }}>
            <div style={{display: "flex", gap: "15px", marginBottom: "15px"}}>
                <div style={{flex: 1}}>
                    <label style={labelStyle}>Select Date</label>
                    <input 
                        type="date" 
                        value={newHoliday.date}
                        onChange={(e) => setNewHoliday({...newHoliday, date: e.target.value})}
                        style={inputStyle} 
                    />
                </div>
                <div style={{flex: 2}}>
                    <label style={labelStyle}>Holiday Description</label>
                    <input 
                        type="text" 
                        placeholder="e.g. Diwali, Independence Day" 
                        value={newHoliday.name}
                        onChange={(e) => setNewHoliday({...newHoliday, name: e.target.value})}
                        style={inputStyle} 
                    />
                </div>
            </div>
            <button type="submit" style={btnAdd} disabled={adding}>
                {adding ? "Adding..." : "➕ Add Holiday"}
            </button>
          </form>

          {/* Holiday List Table */}
          <div style={{ maxHeight: "400px", overflowY: "auto", border: "1px solid #eee", borderRadius: "8px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead style={{ position: "sticky", top: 0, backgroundColor: "#ecf0f1", zIndex: 1 }}>
                <tr>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Occasion</th>
                  <th style={thStyle}>Action</th>
                </tr>
              </thead>
              <tbody>
                {holidays.length === 0 ? (
                    <tr><td colSpan="3" style={{padding:"20px", textAlign:"center", color:"#999"}}>No holidays added yet.</td></tr>
                ) : holidays.map((h) => (
                  <tr key={h.id} style={{ borderBottom: "1px solid #eee", backgroundColor: "white" }}>
                    <td style={{ padding: "12px", fontWeight: "bold", color: "#2c3e50" }}>
                        {new Date(h.date).toLocaleDateString('en-GB', {day:'2-digit', month:'short', year:'numeric'})}
                    </td>
                    <td style={{ padding: "12px", color: "#555" }}>{h.name}</td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                        <button onClick={() => deleteHoliday(h.id)} style={btnDelete}>🗑️ Remove</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}

// 🔥 IMPROVED STYLES
const cardStyle = {
  backgroundColor: "white",
  padding: "25px",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
  flex: 1,
  minWidth: "300px"
};

const headerStyle = {
    borderBottom: "2px solid #f0f0f0", 
    paddingBottom: "15px", 
    marginTop: 0, 
    color: "#34495e",
    fontSize: "18px"
};

const formGroup = { marginBottom: "20px" };

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontWeight: "600",
  fontSize: "13px",
  color: "#7f8c8d",
  textTransform: "uppercase"
};

const inputStyle = {
  width: "100%",
  padding: "12px",
  borderRadius: "6px",
  border: "1px solid #bdc3c7",
  fontSize: "14px",
  boxSizing: "border-box",
  outline: "none",
  transition: "border 0.3s"
};

const thStyle = {
    padding: "12px", 
    textAlign: "left", 
    color: "#7f8c8d", 
    fontWeight: "600",
    borderBottom: "2px solid #ddd"
};

const btnSave = {
  backgroundColor: "#3498db",
  color: "white",
  border: "none",
  padding: "12px",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: "bold",
  width: "100%",
  marginTop: "10px",
  fontSize: "14px",
  transition: "background 0.3s"
};

const btnAdd = {
  backgroundColor: "#27ae60",
  color: "white",
  border: "none",
  padding: "10px 20px",
  borderRadius: "6px",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "14px"
};

const btnDelete = {
    backgroundColor: "#ffebee",
    color: "#c0392b",
    border: "1px solid #ffcdd2",
    padding: "5px 10px",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "bold"
};

export default Settings;